import type { TBulkActionErrorData, TBulkActionUpdateData } from "utils/websockets/types";

import constate from "constate";
import { useSubscriber } from "hooks/useSubscriber";
import { useEffect, useState } from "react";

import { useAdminLiveUpdatesContext } from "./adminLiveUpdatesContext";

const FINISH_DELAY = 1000;

export type TBulkActionNotification =
  | { currentBulkAction: TBulkActionUpdateData; type: "progress" }
  | { type: "done" }
  | { type: "error" }
  | { type: "notInProgress" };

const useBulkActionsUpdates = () => {
  const [isBulkActionInProgress, setIsBulkActionInProgress] = useState(false);
  const [isBulkActionFailed, setIsBulkActionFailed] = useState(false);
  const { subscribeToBulkActionsUpdate, unsubscribeFromBulkActionsUpdate } = useAdminLiveUpdatesContext();
  const { addSubscription, notify, removeSubscription } = useSubscriber<(data: TBulkActionNotification) => void>();

  useEffect(() => {
    function onBulkActionsUpdate(data: null | TBulkActionErrorData | TBulkActionUpdateData) {
      let inProgress = false;
      if (data) {
        if ("error" in data) {
          if (data.error) {
            notify({ type: "error" });
          }
        } else if (data.progressPercentage === 100) {
          notify({
            currentBulkAction: data,
            type: "progress",
          });
          setTimeout(() => {
            notify({ type: "done" });
          }, FINISH_DELAY);
        } else {
          inProgress = true;
          notify({
            currentBulkAction: data,
            type: "progress",
          });
        }
      } else {
        notify({ type: "notInProgress" });
      }
      setIsBulkActionInProgress(inProgress);
    }

    subscribeToBulkActionsUpdate(onBulkActionsUpdate);

    return () => {
      unsubscribeFromBulkActionsUpdate(onBulkActionsUpdate);
    };
  }, [isBulkActionInProgress, notify, subscribeToBulkActionsUpdate, unsubscribeFromBulkActionsUpdate]);

  return {
    isBulkActionFailed,
    isBulkActionInProgress,
    setIsBulkActionFailed,
    setIsBulkActionInProgress,
    subscribeProgression: addSubscription,
    unsubscribeProgression: removeSubscription,
  };
};

export const [BulkActionsUpdatesProvider, useBulkActionsUpdatesContext] = constate(useBulkActionsUpdates);
